import { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useCommandesWebSocket } from '../hooks/useCommandesWebSocket';
import { useChatUnreadCount } from '../hooks/useChatUnreadCount';
import { useTableReservationsPendingCount } from '../hooks/useTableReservationsPendingCount';
import { useNr2jPendingApprovalsCount } from '../hooks/useNr2jPendingApprovalsCount';
import { useMerchantDocumentsActionRequired } from '../pages/documents/useMerchantDocumentsActionRequired';
import Sidebar from './sidebar/Sidebar';
import CommercantSidebarNav from './nav/CommercantSidebarNav';
import SuperAdminSidebarNav from './nav/SuperAdminSidebarNav';
import HeaderMerchantShortcuts from './HeaderMerchantShortcuts';
import AdminNotificationBell from './AdminNotificationBell';
import AssistanceBanner from './AssistanceBanner';
import { buildCommercantNavSections } from '../config/nav/resolveCommercantNav';
import { buildNr2jStaffNavSections, buildSuperAdminNavSections } from '../config/nav/superAdminNav';
import {
  LayoutDashboard,
  LogOut,
  Menu,
  User,
} from 'lucide-react';
import {
  getImpersonatedCommercantId,
  getImpersonatedCommercantLabel,
} from '../utils/impersonation';
import {
  clearAssistanceSession,
  getAssistanceExpiresAt,
} from '../utils/assistanceSession';
import {
  formatRoleLabel,
  getSessionDisplayName,
  readAdminSessionUser,
} from '../utils/settingsSessionUser';

function normalizeRole(role) {
  if (role === 'super-admin' || role === 'SUPER_ADMIN') return 'SUPER_ADMIN';
  if (role === 'commercant' || role === 'COMMERCANT') return 'COMMERCANT';
  return role || null;
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

const Layout = ({ setIsAuthenticated, userRole, impersonatedUser, setImpersonatedUser }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [platformPermissions, setPlatformPermissions] = useState(() => {
    const stored = readAdminSessionUser();
    return Array.isArray(stored?.platformPermissions) ? stored.platformPermissions : [];
  });
  const [commercantData, setCommercantData] = useState(null);
  const [commercantDataLoaded, setCommercantDataLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [storedUser, setStoredUser] = useState(() => readAdminSessionUser());

  const currentRole = useMemo(() => normalizeRole(storedUser?.role), [storedUser]);
  const commercantIdForPermissions = useMemo(
    () => storedUser?.commercantId || storedUser?.commercant?.id || null,
    [storedUser]
  );
  const impersonatedCommercantId = useMemo(
    () => getImpersonatedCommercantId(impersonatedUser),
    [impersonatedUser]
  );
  const isImpersonating = Boolean(impersonatedCommercantId);
  const activeCommercantId = useMemo(
    () => (isImpersonating ? impersonatedCommercantId : commercantIdForPermissions),
    [isImpersonating, impersonatedCommercantId, commercantIdForPermissions]
  );

  useEffect(() => {
    const syncFromStorage = () => {
      const fresh = readAdminSessionUser();
      const changed = !storedUser
        || fresh?.id !== storedUser?.id
        || fresh?.commercantId !== storedUser?.commercantId
        || fresh?.role !== storedUser?.role
        || fresh?.roleInterne !== storedUser?.roleInterne
        || fresh?.prenom !== storedUser?.prenom
        || fresh?.nom !== storedUser?.nom
        || fresh?.email !== storedUser?.email
        || fresh?.telephone !== storedUser?.telephone
        || JSON.stringify(fresh?.staffPermissions) !== JSON.stringify(storedUser?.staffPermissions)
        || JSON.stringify(fresh?.platformPermissions) !== JSON.stringify(storedUser?.platformPermissions);
      if (changed) {
        setStoredUser(fresh);
        if (Array.isArray(fresh?.platformPermissions)) {
          setPlatformPermissions(fresh.platformPermissions);
        }
      }
    };
    const onStorage = (e) => { if (e.key === 'admin_user') syncFromStorage(); };
    const onAuthUpdate = () => syncFromStorage();
    window.addEventListener('storage', onStorage);
    window.addEventListener('authUserUpdated', onAuthUpdate);
    const interval = setInterval(syncFromStorage, 2000);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('authUserUpdated', onAuthUpdate);
      clearInterval(interval);
    };
  }, [storedUser]);

  const { hasPermission: checkPermission, loading: permissionsLoading } = usePermissions(
    (currentRole === 'COMMERCANT' && !impersonatedUser) ? commercantIdForPermissions : null
  );

  const isSuperAdmin = () => {
    if (isImpersonating) return false;
    const effectiveRole = currentRole || userRole;
    return effectiveRole === 'SUPER_ADMIN' || effectiveRole === 'ADMIN';
  };

  const isCommercantOrAdmin = currentRole === 'COMMERCANT' || currentRole === 'SUPER_ADMIN' || currentRole === 'ADMIN';

  const wsCommercantId = activeCommercantId || null;
  const { connected, pendingCount } = useCommandesWebSocket(
    (isCommercantOrAdmin || isImpersonating) && wsCommercantId ? wsCommercantId : null,
    true,
    null,
    null
  );

  const showMerchantHeaderShortcuts = Boolean(
    wsCommercantId && (currentRole === 'COMMERCANT' || isImpersonating)
  );
  /** Cloche plateforme NR2J — Super Admin / NR2J hors assistance (séparée de la cloche boutique). */
  const showPlatformNotificationBell = Boolean(
    !isImpersonating
    && !showMerchantHeaderShortcuts
    && (
      currentRole === 'SUPER_ADMIN'
      || currentRole === 'ADMIN'
      || currentRole === 'NR2J_STAFF'
    )
  );
  const canAccessChat = isImpersonating
    || isSuperAdmin()
    || permissionsLoading
    || checkPermission('GESTION_CHAT');
  const canAccessOrders = isImpersonating
    || isSuperAdmin()
    || permissionsLoading
    || checkPermission('GESTION_COMMANDES');
  const { unreadCount: chatUnreadCount } = useChatUnreadCount(
    showMerchantHeaderShortcuts && canAccessChat ? wsCommercantId : null,
    showMerchantHeaderShortcuts && canAccessChat
  );

  const reservationTableEnabled = Boolean(commercantData?.reservationTable);
  const { pendingCount: tableReservationsPendingCount } = useTableReservationsPendingCount(
    wsCommercantId,
    (isCommercantOrAdmin || isImpersonating) && reservationTableEnabled
  );

  const handleLogout = () => {
    apiService.clearTokens();
    sessionStorage.removeItem('clickorico_impersonating');
    setIsAuthenticated(false);
    setImpersonatedUser(null);
    navigate('/login');
  };

  const handleSidebarNavigation = useCallback((item, { closeMobileSidebar = false } = {}) => {
    if (closeMobileSidebar) setSidebarOpen(false);
    scrollToTop();

    if (item.name === 'Vue d\'ensemble' && activeCommercantId) {
      const targetUrl = `/commercants/${activeCommercantId}`;
      if (location.pathname === targetUrl && (location.search.includes('tab=') || location.search.includes('detail='))) {
        navigate(targetUrl, { replace: true });
        window.dispatchEvent(new CustomEvent('tabChange', { detail: { tab: 'overview' } }));
      } else {
        navigate(targetUrl);
      }
      return;
    }

    if ((currentRole === 'COMMERCANT' || isImpersonating) && item.tab && activeCommercantId) {
      if (item.permission) {
        const hasAccess = isSuperAdmin() || checkPermission(item.permission);
        if (!hasAccess && !permissionsLoading) {
          navigate(`/commercants/${activeCommercantId}?tab=${item.tab}`);
          return;
        }
      }
      navigate(`/commercants/${activeCommercantId}?tab=${item.tab}`);
      return;
    }

    navigate(item.href);
  }, [
    activeCommercantId,
    checkPermission,
    currentRole,
    isImpersonating,
    location.pathname,
    location.search,
    navigate,
    permissionsLoading,
  ]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let cid = null;
      if (impersonatedCommercantId) {
        cid = impersonatedCommercantId;
      } else if (currentRole === 'COMMERCANT') {
        cid = commercantIdForPermissions;
      }
      if (!cid) {
        setCommercantDataLoaded(true);
        return;
      }
      try {
        const data = await apiService.getCommercant(cid);
        if (!cancelled) {
          setCommercantData(data);
          setCommercantDataLoaded(true);
        }
      } catch (error) {
        console.error('[Layout] Erreur chargement données commerçant:', error);
        if (!cancelled) setCommercantDataLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [commercantIdForPermissions, impersonatedCommercantId, currentRole]);

  useEffect(() => {
    if (impersonatedUser) {
      sessionStorage.setItem('clickorico_impersonating', '1');
    } else {
      sessionStorage.removeItem('clickorico_impersonating');
    }
  }, [impersonatedUser]);

  const exitAssistance = useCallback(async () => {
    const assistedId = impersonatedCommercantId;
    try {
      await apiService.endAssistance();
    } catch {
      clearAssistanceSession();
    }
    setImpersonatedUser(null);
    if (assistedId) {
      navigate(`/commercants/${assistedId}`);
    } else {
      navigate('/commercants');
    }
  }, [impersonatedCommercantId, navigate, setImpersonatedUser]);

  // Si navigation vers un autre commerçant pendant l'assistance → sortie propre (token inclus).
  useEffect(() => {
    if (!impersonatedCommercantId || !setImpersonatedUser) return;
    const match = location.pathname.match(/^\/commercants\/([^/?]+)/);
    if (match && match[1] !== impersonatedCommercantId) {
      void (async () => {
        try {
          await apiService.endAssistance();
        } catch {
          clearAssistanceSession();
        }
        setImpersonatedUser(null);
      })();
    }
  }, [location.pathname, impersonatedCommercantId, setImpersonatedUser]);

  // Expiration TTL : purge locale + retour détail commerçant assisté.
  useEffect(() => {
    if (!isImpersonating) return undefined;
    const expiresAt = getAssistanceExpiresAt();
    if (!expiresAt) return undefined;
    const ms = Date.parse(expiresAt) - Date.now();
    if (Number.isNaN(ms)) return undefined;
    if (ms <= 0) {
      void exitAssistance();
      return undefined;
    }
    const timer = setTimeout(() => {
      void exitAssistance();
    }, ms);
    return () => clearTimeout(timer);
  }, [isImpersonating, impersonatedUser, exitAssistance]);

  useEffect(() => {
    const handleServicesUpdate = (event) => {
      const { commercantId } = event.detail;
      if (currentRole === 'COMMERCANT' && commercantIdForPermissions === commercantId) {
        apiService.getCommercant(commercantId).then(setCommercantData).catch(() => {});
      }
    };
    window.addEventListener('commercantServicesUpdated', handleServicesUpdate);
    return () => window.removeEventListener('commercantServicesUpdated', handleServicesUpdate);
  }, [currentRole, commercantIdForPermissions]);

  const merchantNavActive = normalizeRole(
    isImpersonating ? 'COMMERCANT' : (currentRole || userRole),
  ) === 'COMMERCANT';
  const documentsActionRequiredCount = useMerchantDocumentsActionRequired(merchantNavActive);

  const commercantNavSections = useMemo(() => {
    const effectiveRole = isImpersonating ? 'COMMERCANT' : (currentRole || userRole);
    if (normalizeRole(effectiveRole) !== 'COMMERCANT') return null;

    const commercantId = isImpersonating ? impersonatedCommercantId : commercantIdForPermissions;
    return buildCommercantNavSections({
      commercantId,
      commercantData,
      commercantDataLoaded,
      storedUser,
    });
  }, [
    commercantData,
    commercantDataLoaded,
    commercantIdForPermissions,
    impersonatedCommercantId,
    isImpersonating,
    userRole,
    currentRole,
    storedUser,
  ]);

  useEffect(() => {
    if (normalizeRole(currentRole || userRole) !== 'NR2J_STAFF' || isImpersonating) return undefined;
    let cancelled = false;

    const hydratePlatformAccess = () => {
      apiService.getMyPlatformAccess()
        .then((access) => {
          if (cancelled) return;
          const permissions = Array.isArray(access?.permissions) ? access.permissions : [];
          const fresh = readAdminSessionUser() || {};
          const next = {
            ...fresh,
            platformProfile: access?.platformProfile ?? fresh.platformProfile ?? null,
            platformPermissions: permissions,
            discountApprovalThresholdBps:
              access?.discountApprovalThresholdBps !== undefined
                ? access.discountApprovalThresholdBps
                : (fresh.discountApprovalThresholdBps ?? null),
          };
          localStorage.setItem('admin_user', JSON.stringify(next));
          setPlatformPermissions(permissions);
          setStoredUser(next);
        })
        .catch(() => {
          if (cancelled) return;
          const fresh = readAdminSessionUser() || {};
          if (!Array.isArray(fresh.platformPermissions)) {
            const next = { ...fresh, platformPermissions: [] };
            localStorage.setItem('admin_user', JSON.stringify(next));
            setPlatformPermissions([]);
            setStoredUser(next);
          }
        });
    };

    // Toujours recharger les permissions effectives (autonomie / overrides)
    // — ne pas se fier uniquement au snapshot login (stale après toggle SA).
    hydratePlatformAccess();

    const onVisible = () => {
      if (document.visibilityState === 'visible') hydratePlatformAccess();
    };
    document.addEventListener('visibilitychange', onVisible);

    const onAuthUserUpdated = (event) => {
      const next = event?.detail || readAdminSessionUser() || {};
      if (!Array.isArray(next.platformPermissions)) {
        hydratePlatformAccess();
        return;
      }
      setPlatformPermissions(next.platformPermissions);
      setStoredUser(next);
    };
    window.addEventListener('authUserUpdated', onAuthUserUpdated);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('authUserUpdated', onAuthUserUpdated);
    };
  }, [currentRole, userRole, isImpersonating]);

  const superAdminNavSections = useMemo(() => {
    const effectiveRole = isImpersonating ? 'COMMERCANT' : (currentRole || userRole);
    const normalizedRole = normalizeRole(effectiveRole);
    if (normalizedRole === 'NR2J_STAFF' && !isImpersonating) {
      return buildNr2jStaffNavSections(platformPermissions, {
        platformProfile: storedUser?.platformProfile || null,
        role: 'NR2J_STAFF',
      });
    }
    if ((normalizedRole === 'SUPER_ADMIN' || normalizedRole === 'ADMIN') && !isImpersonating) {
      return buildSuperAdminNavSections();
    }
    return null;
  }, [currentRole, userRole, isImpersonating, platformPermissions, storedUser?.platformProfile]);

  const canSeeNr2jApprovals = useMemo(() => {
    if (isImpersonating) return false;
    const role = normalizeRole(currentRole || userRole);
    return role === 'SUPER_ADMIN'
      || (role === 'NR2J_STAFF' && storedUser?.platformProfile === 'MANAGER_NR2J');
  }, [currentRole, userRole, isImpersonating, storedUser?.platformProfile]);
  const { pendingCount: nr2jPendingApprovalsCount } = useNr2jPendingApprovalsCount(canSeeNr2jApprovals);
  const superAdminBadgeCounts = useMemo(
    () => ({ 'nr2j-a-valider': nr2jPendingApprovalsCount }),
    [nr2jPendingApprovalsCount],
  );

  const fallbackNavSections = useMemo(
    () => [{
      id: 'dashboard',
      title: null,
      items: [{ id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }],
    }],
    []
  );

  const renderSidebarNav = (closeMobileSidebar = false) => {
    const onNavigate = (item) => handleSidebarNavigation(item, { closeMobileSidebar });

    if (commercantNavSections) {
      return (
        <CommercantSidebarNav
          sections={commercantNavSections}
          location={location}
          onNavigate={onNavigate}
          reservationTableEnabled={reservationTableEnabled}
          commandesBadgeProps={{ pendingCount, connected }}
          tableReservationsPendingCount={tableReservationsPendingCount}
          documentsActionRequiredCount={documentsActionRequiredCount}
        />
      );
    }

    return (
      <SuperAdminSidebarNav
        sections={superAdminNavSections || fallbackNavSections}
        location={location}
        onNavigate={onNavigate}
        badgeCounts={superAdminBadgeCounts}
      />
    );
  };

  const isLoginPage = location.pathname === '/login';
  const isSettingsPage = location.pathname === '/settings';
  const containerMaxWidth = isLoginPage ? 'max-w-md' : 'max-w-[1800px]';

  return (
    <div className={`min-h-screen ${isSettingsPage ? 'bg-[#f5f4f7]' : 'bg-gray-50'}`}>
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <Sidebar
          className="fixed inset-y-0 left-0"
          showCloseButton
          onClose={() => setSidebarOpen(false)}
        >
          {renderSidebarNav(true)}
        </Sidebar>
      </div>

      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <Sidebar className="min-h-full flex-grow border-r border-gray-200">
          {renderSidebarNav(false)}
        </Sidebar>
      </div>

      <div className="min-w-0 lg:pl-64">
        {isImpersonating && (
          <AssistanceBanner
            commercantLabel={getImpersonatedCommercantLabel(impersonatedUser)}
            expiresAt={getAssistanceExpiresAt()}
            onExit={exitAssistance}
          />
        )}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-3 lg:gap-x-4">
              {showPlatformNotificationBell ? (
                <AdminNotificationBell enabled variant="platform" />
              ) : null}
              {showMerchantHeaderShortcuts ? (
                <>
                  <AdminNotificationBell enabled={showMerchantHeaderShortcuts} variant="merchant" />
                  <HeaderMerchantShortcuts
                    showChat={canAccessChat}
                    showOrders={canAccessOrders}
                    chatCount={chatUnreadCount}
                    ordersCount={pendingCount}
                    onOpenChat={() => {
                      scrollToTop();
                      if (activeCommercantId) {
                        navigate(`/commercants/${activeCommercantId}?tab=chat`);
                      } else {
                        navigate('/chat');
                      }
                    }}
                    onOpenOrders={() => {
                      scrollToTop();
                      if (activeCommercantId) {
                        navigate(`/commercants/${activeCommercantId}?tab=commandes`);
                      } else {
                        navigate('/commandes');
                      }
                    }}
                  />
                </>
              ) : null}
              <div className="flex items-center gap-x-2">
                <User className="h-5 w-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  {isImpersonating
                    ? getImpersonatedCommercantLabel(impersonatedUser)
                    : getSessionDisplayName(storedUser)}
                </span>
                {!isImpersonating && currentRole && currentRole !== 'COMMERCANT' ? (
                  <span className="text-xs text-primary-700 bg-primary-50 px-2 py-1 rounded-full">
                    {formatRoleLabel(storedUser?.role, storedUser?.platformProfile)}
                  </span>
                ) : null}
                {isImpersonating && (
                  <span className="text-xs text-amber-800 bg-amber-100 px-2 py-1 rounded-full">
                    Mode assistance
                  </span>
                )}
                {currentRole === 'COMMERCANT' && !impersonatedUser && (() => {
                  const roleLabels = { GERANT: 'Gérant', MANAGER: 'Manager', VENDEUR: 'Vendeur', CAISSIER: 'Caissier' };
                  const roleColors = {
                    GERANT: 'text-purple-700 bg-purple-100',
                    MANAGER: 'text-blue-700 bg-blue-100',
                    VENDEUR: 'text-green-700 bg-green-100',
                    CAISSIER: 'text-orange-700 bg-orange-100',
                  };
                  const label = roleLabels[storedUser?.roleInterne] || 'Commerçant';
                  const color = roleColors[storedUser?.roleInterne] || 'text-green-600 bg-green-100';
                  return (
                    <span className={`text-xs ${color} px-2 py-1 rounded-full`}>
                      {label}
                    </span>
                  );
                })()}
              </div>
              {isImpersonating && (
                <button
                  type="button"
                  onClick={exitAssistance}
                  className="flex items-center gap-x-2 text-sm text-amber-800 hover:text-amber-950"
                >
                  <User className="h-5 w-5" />
                  Quitter l&apos;assistance
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-x-2 text-sm text-gray-700 hover:text-gray-900"
              >
                <LogOut className="h-5 w-5" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>

        <main className="min-w-0 overflow-x-hidden py-6">
          <div className={`mx-auto min-w-0 ${containerMaxWidth} px-4 sm:px-6 lg:px-8`}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
